import { createApp, reactive, nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import miniToastr from 'mini-toastr'
import VueGoodTablePlugin from 'vue-good-table-next'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import './scss/libs.scss'
import './js/theme.js'
import './scss/theme.scss'
import routes from './routes'
import { services } from './services'
import { EventBus } from './bus'
import filters from './filters'
import App from './App.vue'

// Notifications setup
const toastTypes = {
    success: 'success',
    error: 'error',
    info: 'info',
    warn: 'warn'
}
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
    if (type == 'error')
        timeout = 100000000000;
    return miniToastr[type](message, title, timeout, cb)
}

// SEO Helper functions
const defaultMeta = {
    title: 'DataLikers - Instagram & TikTok Data API',
    description: 'Access Instagram and TikTok data at scale. Fast Cache API, AI-powered MCP Server for Claude, and ready-made datasets. No rate limits, no blocks.',
    keywords: 'Instagram API, TikTok API, social media data, Instagram scraper, TikTok scraper, Instagram datasets',
    image: 'https://datalikers.com/assets/img/brand.png'
}

function updateMetaTag(name, content, isProperty = false) {
    const attr = isProperty ? 'property' : 'name'
    let element = document.querySelector(`meta[${attr}="${name}"]`)
    if (element) {
        element.setAttribute('content', content)
    } else {
        element = document.createElement('meta')
        element.setAttribute(attr, name)
        element.setAttribute('content', content)
        document.head.appendChild(element)
    }
}

function updateCanonicalUrl(path) {
    const baseUrl = 'https://datalikers.com'
    const canonicalUrl = path === '/' ? baseUrl + '/' : baseUrl + path
    let link = document.querySelector('link[rel="canonical"]')
    if (link) {
        link.setAttribute('href', canonicalUrl)
    }
    updateMetaTag('og:url', canonicalUrl, true)
    updateMetaTag('twitter:url', canonicalUrl)
}

function updateSEOMeta(meta, path) {
    const siteName = document.title.split('|')[0].trim()
    const title = meta.title ? `${siteName} | ${meta.title}` : siteName
    const description = meta.description || defaultMeta.description
    const keywords = meta.keywords || defaultMeta.keywords

    // Update title
    document.title = title

    // Update primary meta tags
    updateMetaTag('title', title)
    updateMetaTag('description', description)
    updateMetaTag('keywords', keywords)

    // Update robots meta
    if (meta.noindex) {
        updateMetaTag('robots', 'noindex, nofollow')
    } else {
        updateMetaTag('robots', 'index, follow')
    }

    // Update Open Graph
    updateMetaTag('og:title', meta.title || defaultMeta.title, true)
    updateMetaTag('og:description', description, true)

    // Update Twitter
    updateMetaTag('twitter:title', meta.title || defaultMeta.title)
    updateMetaTag('twitter:description', description)

    // Update canonical URL
    updateCanonicalUrl(path)

    // Update structured data
    updateStructuredData(meta, path)
}

function updateStructuredData(meta, path) {
    const baseUrl = 'https://datalikers.com'
    const canonicalUrl = path === '/' ? `${baseUrl}/` : `${baseUrl}${path}`
    const title = meta.title ? `DataLikers | ${meta.title}` : defaultMeta.title
    const description = meta.description || defaultMeta.description
    const noindex = meta.noindex === true

    const graph = []

    // WebPage for indexable pages
    if (!noindex) {
        graph.push({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: title,
            description,
            url: canonicalUrl
        })
    }

    // BreadcrumbList for non-root public pages
    if (!noindex && path !== '/') {
        const breadcrumbNames = {
            features: 'Features', faq: 'FAQ', 'cache-api': 'Cache API', mcp: 'MCP Server',
            datasets: 'Datasets', status: 'API Status', pricing: 'Pricing', review: 'Reviews',
            registration: 'Sign Up', docs: 'Documentation', help: 'Help Center',
            'dataset-builder': 'Dataset Builder'
        }
        const segments = path.split('/').filter(Boolean)
        const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${baseUrl}/` }]
        let currentPath = ''
        segments.forEach((seg, i) => {
            currentPath += `/${seg}`
            items.push({ '@type': 'ListItem', position: i + 2, name: breadcrumbNames[seg] || meta.title || seg, item: `${baseUrl}${currentPath}` })
        })
        graph.push({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items })
    }

    // Product schema for /cache-api
    if (path === '/cache-api') {
        graph.push({
            '@context': 'https://schema.org',
            '@type': 'WebAPI',
            name: 'DataLikers Instagram Cache API',
            description: 'REST API to access Instagram user profiles, posts, comments, stories, reels, hashtags and locations. Cached data with instant responses, no rate limits.',
            url: `${baseUrl}/cache-api`,
            documentation: `https://api.datalikers.com/docs`,
            provider: {
                '@type': 'Organization',
                name: 'DataLikers',
                url: baseUrl
            },
            offers: [
                { '@type': 'Offer', name: 'Outdated', price: '0.0003', priceCurrency: 'USD', description: 'Cached data regardless of age' },
                { '@type': 'Offer', name: 'Month', price: '0.0004', priceCurrency: 'USD', description: 'Data not older than a month' },
                { '@type': 'Offer', name: 'Week', price: '0.0005', priceCurrency: 'USD', description: 'Data not older than a week' },
                { '@type': 'Offer', name: 'Day', price: '0.0006', priceCurrency: 'USD', description: 'Data not older than one day' }
            ],
            termsOfService: `${baseUrl}/help/terms-of-service`
        })
    }

    // WebAPI schema for /mcp
    if (path === '/mcp') {
        graph.push({
            '@context': 'https://schema.org',
            '@type': 'WebAPI',
            name: 'DataLikers Instagram MCP Server',
            description: 'Model Context Protocol server for Instagram data. 27 tools for querying user profiles, posts, comments, stories, hashtags, demographics, and face analysis. Works with Claude AI, Cursor, and other MCP-compatible clients.',
            url: `${baseUrl}/mcp`,
            documentation: `${baseUrl}/docs/mcp`,
            provider: {
                '@type': 'Organization',
                name: 'DataLikers',
                url: baseUrl
            },
            offers: {
                '@type': 'Offer',
                price: '0.0003',
                priceCurrency: 'USD',
                description: 'Pay per request, from $0.0003'
            }
        })
        graph.push({
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to connect Instagram MCP Server to Claude AI',
            step: [
                { '@type': 'HowToStep', position: 1, name: 'Get API key', text: 'Register at DataLikers and get your API access key from the dashboard.' },
                { '@type': 'HowToStep', position: 2, name: 'Configure MCP', text: 'Add the MCP server URL (mcp.datalikers.com) and your API key to Claude Desktop settings.' },
                { '@type': 'HowToStep', position: 3, name: 'Query Instagram data', text: 'Ask Claude to search Instagram users, analyze demographics, view posts, and more using natural language.' }
            ]
        })
    }

    // Dataset schema for /datasets
    if (path === '/datasets') {
        graph.push({
            '@context': 'https://schema.org',
            '@type': 'DataCatalog',
            name: 'DataLikers Instagram Datasets',
            description: 'Ready-made Instagram datasets with millions of user profiles, posts, comments, and engagement metrics. CSV format, instant download.',
            url: `${baseUrl}/datasets`,
            provider: {
                '@type': 'Organization',
                name: 'DataLikers',
                url: baseUrl
            },
            dataset: [
                {
                    '@type': 'Dataset',
                    name: 'Instagram Users Dataset',
                    description: 'Instagram user profiles with followers, following, bio, business contacts, and engagement metrics.',
                    distribution: { '@type': 'DataDownload', encodingFormat: 'text/csv' }
                },
                {
                    '@type': 'Dataset',
                    name: 'Instagram Posts Dataset',
                    description: 'Instagram posts with likes, comments, captions, media URLs, and hashtags.',
                    distribution: { '@type': 'DataDownload', encodingFormat: 'text/csv' }
                },
                {
                    '@type': 'Dataset',
                    name: 'Instagram Comments Dataset',
                    description: 'Instagram comments with text, timestamps, and user information.',
                    distribution: { '@type': 'DataDownload', encodingFormat: 'text/csv' }
                }
            ]
        })
    }

    // FAQPage schema is injected by SSR (see lm/steve/api/capi/routers/pages.py + PAGE_FAQ_DATA in config.py)

    if (graph.length === 0) return

    let script = document.querySelector('script[data-seo-ld="dynamic"]')
    if (!script) {
        script = document.createElement('script')
        script.setAttribute('type', 'application/ld+json')
        script.setAttribute('data-seo-ld', 'dynamic')
        document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(graph)
}

function buildTitle(title) {
    document.title = `${document.title.split('|')[0]} | ${title}`
}

const unsecuredCopyToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
        document.execCommand('copy');
    } catch(err) {
        console.error('Unable to copy to clipboard', err);
    }
    document.body.removeChild(textArea);
};

window.getFilter = function(namespace, keyName, defaultValue) {
    let data = JSON.parse(localStorage.getItem(namespace + '_filter') || '{}');
    if (keyName) {
        return data[keyName] || defaultValue
    }
    return data
}
window.setFilter = function(namespace, data) {
    let current = window.getFilter(namespace);
    for (let key in data)
        current[key] = data[key];
    localStorage.setItem(namespace + '_filter', JSON.stringify(current));
    return true;
}

// Create router
const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'active',
    scrollBehavior(to, from, savedPosition) {
        return { left: 0, top: 0 }
    }
})

router.beforeEach((to, from, next) => {
    // Update SEO meta tags for each route
    updateSEOMeta(to.meta || {}, to.path)
    next()
})

// Create app
const app = createApp(App)

// Global state (replacing root data)
const globalState = reactive({
    context: {
        loaded: null,
    },
    rubRate: null,
    siteProto: env.PROTOCOL,
    siteDomain: env.DOMAIN,
    siteName: env.SITE_NAME,
    siteEmail: env.SITE_EMAIL,
    siteTgSupport: env.TG_SUPPORT,
    siteTgBase: env.TG_BASE || 'https://t.me',
    footerCreds: env.FOOTER_CREDS ? JSON.parse(env.FOOTER_CREDS) : [],
    cfSiteKey: env.CF_SITE_KEY,
    capiUrl: "/api/",
})

// Global methods
const globalMethods = {
    copyToClipboard(content) {
        if (window.isSecureContext && navigator.clipboard) {
            navigator.clipboard.writeText(content);
        } else {
            unsecuredCopyToClipboard(content);
        }
        return true;
    },
    raise404() {
        router.push({ name: '404' });
    },
    raise500() {
        router.push({ name: '500' });
    },
    getContext() {
        services.Context().get().then(
            resp => globalMethods.updateContext(resp.body),
            _ => globalMethods.updateContext({})
        );
    },
    informContext() {
        EventBus.emit('context:loaded', globalState.context);
    },
    updateContext(context) {
        const deflt = {
            loaded: true,
            promocode: localStorage.getItem("promocode"),
            referrer: localStorage.getItem("referrer")
        }
        Object.assign(globalState.context, deflt, context);
        globalMethods.informContext();
    },
    buildTitle
}

// Provide globals via app.config.globalProperties
app.config.globalProperties.$state = globalState
app.config.globalProperties.$services = services
app.config.globalProperties.$methods = globalMethods
app.config.globalProperties.$filters = filters
app.config.globalProperties.$bus = EventBus

// Notification plugin
app.config.globalProperties.$notify = {
    success: (opts) => toast({ ...opts, type: 'success' }),
    error: (opts) => toast({ ...opts, type: 'error' }),
    info: (opts) => toast({ ...opts, type: 'info' }),
    warn: (opts) => toast({ ...opts, type: 'warn' }),
}

// Register global filters as methods (Vue 3 doesn't have filters)
app.config.globalProperties.$linebreaks = filters.linebreaks
app.config.globalProperties.$percentage = filters.percentage

// Use plugins
app.use(router)
app.use(VueGoodTablePlugin)

// Mount app
app.mount('#app')

// Initialize context
globalMethods.getContext();

// WebMCP — expose page-level actions to in-browser AI agents.
// No-op on browsers that don't ship document.modelContext.
import('./utils/webmcp.js').then(({ registerWebMCP }) => {
    registerWebMCP(router);
}).catch(() => { /* silently skip — non-critical */ });

// Load RUB exchange rate
services.CurrencyRate().get().then(
    resp => { globalState.rubRate = resp.body.rate },
    _ => {}
);

// Setup event listeners
EventBus.on('context:changed', globalMethods.getContext);
EventBus.on('context:updated', globalMethods.updateContext);
EventBus.on('context:request', globalMethods.informContext);

// Export for potential use
export { app, router, globalState, globalMethods, services }
